import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

const controlBase =
  'focus-ring block w-full rounded-lg border border-line-input bg-card px-3 py-2 text-sm text-ink placeholder:text-ink-faint disabled:bg-page disabled:text-ink-faint';

export function Label({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-ink-strong">
      {children}
    </label>
  );
}

/** Wraps a labelled control with optional help/error text. */
export function FormField({
  label,
  htmlFor,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  /** Show the field is required. Marks the label, and says so to a screen reader. */
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor}>
        {label}
        {required && (
          <>
            <span aria-hidden className="ml-0.5 text-red-500 dark:text-error-dark">
              *
            </span>
            <span className="sr-only"> (required)</span>
          </>
        )}
      </Label>
      {children}
      {error ? (
        <p className="mt-1 text-xs text-red-600 dark:text-error-dark">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-ink-faint">{hint}</p>
      ) : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cn(controlBase, className)} {...rest} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cn(controlBase, 'resize-y', className)} {...rest} />;
});

/**
 * Re-exported so every existing `import { Select } from '.../Field'` keeps working. The
 * implementation moved to ./Select when dropdowns stopped using the browser's own option list —
 * see that file for why.
 */
export { Select } from './Select';
